import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Fetch } from "../../shared/Fetch";
import { ILogin } from "../../models/Login";
import { safeFormatDate } from "../../models/DateFormatMapper";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Dialog } from "primereact/dialog";
import { format } from "date-fns";
import { IPurchaseOrder, POStatus } from "../../models/PurchaseOrder";
import { Accordion, AccordionTab } from "primereact/accordion";
import { EmptyRateCahngeFilter, Filter, IRateChangeFilter } from "./Filter";
import { POStatusMap } from "../../models/FDropdown";

export interface IPoRateChange {
  id: number;
  pO_ID?: number;
  p_BRate?: number;
  p_SRate?: number;
  p_MRate?: number;
  r_BRate?: number;
  r_SRate?: number;
  r_MRate?: number;
  created_On?: string;
  created_By?: number;
  branch_Action_By?: string;
  admin_Action_By?: string;
  admin_Action_Reason?: string;
  branch_Action_Reason?: string;
  branch_Action_Status?: boolean;
  admin_Action_Status?: boolean;
}

export const Table = (props: any) => {
  const [tableData, setTableData] = useState<{ tableData: IPoRateChange[]; loading: boolean }>({ tableData: [], loading: false });
  const [reasonDialog, setReasonDialog] = useState({ visible: false, reason: "", title: "" });
  const [openPODetails, setOpenPODetails] = useState({ visible: false, singleData: {} } as { visible: boolean; singleData: IPurchaseOrder });
  const [filters, setFilters] = useState<IRateChangeFilter>(EmptyRateCahngeFilter);
  const [dialog, setDialog] = useState<{
    open: boolean;
    rowData: IPoRateChange | null;
    action: "approve" | "reject" | null;
    approvalType: "branch" | "admin" | null;
    reason: string;
  }>({
    open: false,
    rowData: null,
    action: null,
    approvalType: null,
    reason: "",
  });

  const [approvalLoading, setApprovalLoading] = useState(false);

  const dt = useRef<any>(null);

  const loadTableData = async () => {
    try {
      setTableData((prev) => ({ ...prev, loading: true }));

      const response = await Fetch("Po_Rate_Change", "GET", null, `filter=${JSON.stringify(filters)}`, props.toast);

      if (response) {
        const data = await response.json();
        setTableData({ tableData: data, loading: false });
      }
    } catch {
      setTableData((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    loadTableData();
  }, [filters]);

  const header = (
    <div className="flex justify-content-between">
      <h5 className="m-0">PO Rate Approval</h5>
    </div>
  );

  const loginObj: ILogin = JSON.parse(sessionStorage.getItem("auth") + "");
  const DateFormate = loginObj?.date_Format;

  const _roles: Array<number> = [];
  loginObj.roles.forEach((role) => _roles.push(role.role_ID));
  const isBranchManager = _roles.includes(11);
  const isAdmin = _roles.includes(1);

  console.log("isBranchManager:", isBranchManager, "isAdmin:", isAdmin);

  const openDialog = (rowData: IPoRateChange, action: "approve" | "reject", approvalType: "branch" | "admin") => {
    setDialog({ open: true, rowData, action, approvalType, reason: "" });
  };

  const hideDialog = () => {
    setDialog({ open: false, rowData: null, action: null, approvalType: null, reason: "" });
  };

  const handleSubmit = async () => {
    if (dialog.action === "reject" && !dialog.reason.trim()) {
      props.toast.current.show({ severity: "error", content: "Reason is required for rejection." });
      return;
    }
    setApprovalLoading(true);
    const body = {
      id: dialog.rowData?.id,
      action: dialog.action,
      approvalType: dialog.approvalType,
      reason: dialog.reason,
    };
    const response = await Fetch("Po_Rate_Change/approve", "POST", body, null, props.toast);
    if (response) {
      props.toast.current.show({ severity: "success", content: `${dialog.action === "approve" ? "Approved" : "Rejected"} successfully.` });
      hideDialog();
      loadTableData();
    }
    setApprovalLoading(false);
  };

  const getPODetails = async (poID: number) => {
    const response = await Fetch("Po_Rate_Change/getPODetails", "GET", null, `poID=${poID}`, props.toast);
    if (response) {
      const data: IPurchaseOrder = await response.json();
      setOpenPODetails({ visible: true, singleData: data });
    }
  };

  return (
    <div>
      <Accordion>
        <AccordionTab header="Filter">
          <Filter filters={filters} setFilters={(filters: IRateChangeFilter) => setFilters(filters)} />
        </AccordionTab>
      </Accordion>
      <DataTable ref={dt} value={tableData.tableData} showGridlines loading={tableData.loading} stripedRows scrollable scrollHeight="90vh" emptyMessage="No Records found." header={header} size="small" className="mt-2 border-round shadow-2">
        <Column header="SN" body={(rowData, options) => options.rowIndex + 1} frozen style={{ width: "3rem", maxWidth: "3rem", overflow: "hidden" }} headerStyle={{ width: "4rem", maxWidth: "4rem" }} bodyStyle={{ width: "3rem", maxWidth: "3rem", textAlign: "center" }} />
        <Column
          field="pO_ID"
          header="PO ID"
          style={{ minWidth: "50px" }}
          body={(rowData: any) => (
            <p
              onClick={() => {
                if (rowData.pO_ID !== undefined && rowData.pO_ID !== null && rowData.pO_ID > 0) getPODetails(rowData.pO_ID);
              }}
              className="cursor-pointer text-cyan-700 underline"
            >
              {rowData.pO_ID}
            </p>
          )}
        />
        <Column field="p_BRate" header="Previous B Rate" style={{ minWidth: "50px" }} />
        <Column field="p_SRate" header="Previous S Rate" style={{ minWidth: "80px" }} />
        <Column field="p_MRate" header="Previous M Rate" style={{ minWidth: "80px" }} />
        <Column field="r_BRate" header="Requested B Rate" style={{ minWidth: "100px" }} />
        <Column field="r_SRate" header="Requested S Rate" style={{ minWidth: "100px" }} />
        <Column field="r_MRate" header="Requested M Rate" style={{ minWidth: "100px" }} />
        <Column field="created_By" header="Requested By" style={{ minWidth: "110px" }} />
        <Column header="Created On" style={{ minWidth: "110px" }} body={(rowData: any) => safeFormatDate(rowData.created_On, "dd/MM/yyyy", DateFormate)} />

        <Column
          field="branch_Action_By"
          header="Branch Action Status"
          style={{ minWidth: "160px" }}
          body={(rowData) => {
            const branchPending = rowData.branch_Action_Status === null || rowData.branch_Action_Status === undefined;
            const branchRejected = rowData.branch_Action_Status === false;

            if (branchRejected) {
              return (
                <div className="flex flex-column gap-1">
                  <span className=" font-bold" style={{ color: "red" }}>
                    ✘ Rejected
                  </span>
                  {rowData.branch_Action_Reason && (
                    <span className="mt-1 p-button-sm p-link text-blue-500" onClick={() => setReasonDialog({ visible: true, reason: rowData.branch_Action_Reason, title: "Branch Rejection Reason" })}>
                      View Reason
                    </span>
                  )}
                </div>
              );
            }

            if (branchRejected) return <span className="text-red-500 font-bold">✘ Rejected</span>;
            if (!branchPending) return <span className="text-green-500 font-bold">✔ Approved</span>;

            if (!isBranchManager && !isAdmin) return "⏳ Pending";
            if (isBranchManager && branchPending) {
              return (
                <div className="flex flex-column gap-1">
                  <Button label="Approve" icon="pi pi-check" className="p-button-success p-button-sm w-full" onClick={() => openDialog(rowData, "approve", "branch")} />
                  <Button label="Reject" icon="pi pi-times" className="p-button-danger p-button-sm w-full" onClick={() => openDialog(rowData, "reject", "branch")} />
                </div>
              );
            }

            return <span className="text-yellow-500 font-bold">⏳ Pending</span>;
          }}
        />

        <Column field="branch_Action_By" header="Action By Branch Head" style={{ minWidth: "100px" }} />

        <Column
          field="admin_Action_By"
          header="Admin Action Status"
          style={{ minWidth: "160px" }}
          body={(rowData) => {
            const adminPending = rowData.admin_Action_Status === null || rowData.admin_Action_Status === undefined;
            const adminRejected = rowData.admin_Action_Status === false;
            const branchRejected = rowData.branch_Action_Status === false;

            if (adminRejected) {
              return (
                <div className="flex flex-column gap-1">
                  <span className=" font-bold" style={{ color: "red" }}>
                    ✘ Rejected
                  </span>
                  {rowData.admin_Action_Reason && (
                    <span className="mt-1 p-button-sm p-link text-blue-500" onClick={() => setReasonDialog({ visible: true, reason: rowData.admin_Action_Reason, title: "Admin Rejection Reason" })}>
                      View Reason
                    </span>
                  )}
                </div>
              );
            }

            if (branchRejected) {
              return (
                <div className="flex flex-column gap-1">
                  <span className=" font-bold" style={{ color: "red" }}>
                    ✘ Rejected
                  </span>
                  {rowData.branch_Action_Reason && (
                    <span className="mt-1 p-button-sm p-link text-blue-500" onClick={() => setReasonDialog({ visible: true, reason: rowData.branch_Action_Reason, title: "Branch Rejection Reason" })}>
                      View Reason
                    </span>
                  )}
                </div>
              );
            }

            if (adminRejected) return <span className="text-red-500 font-bold">✘ Rejected</span>;
            if (!adminPending) return <span className="text-green-500 font-bold">✔ Approved</span>;
            if (rowData.branch_Action_Status === null) return <span className="text-blue-500 font-bold">⏳ Waiting for Branch</span>;

            if (!isBranchManager && !isAdmin) return "⏳ Pending";
            if (isAdmin && adminPending && rowData.branch_Action_Status !== null) {
              return (
                <div className="flex flex-column gap-1">
                  <Button label="Approve" icon="pi pi-check" className="p-button-success p-button-sm w-full" onClick={() => openDialog(rowData, "approve", "admin")} />
                  <Button label="Reject" icon="pi pi-times" className="p-button-danger p-button-sm w-full" onClick={() => openDialog(rowData, "reject", "admin")} />
                </div>
              );
            }

            return <span className="text-yellow-500 font-bold">⏳ Pending</span>;
          }}
        />
        <Column field="admin_Action_By" header="Action By Admin" style={{ minWidth: "100px" }} />
      </DataTable>

      {/* Dialog Box  Approve and Reject */}
      <Dialog
        visible={dialog.open}
        onHide={hideDialog}
        header={
          dialog.action === "approve" ? (
            <span style={{ color: "green" }}>
              <i className="pi pi-check-circle mr-2" />
              Approve Rate Change
            </span>
          ) : (
            <span style={{ color: "black" }}>
              <i className="mr-2" />
              Reject Rate Change
            </span>
          )
        }
        style={{ width: "35rem" }}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon="pi pi-times" className="p-button-outlined p-button-sm" onClick={hideDialog} />
            <Button label={dialog.action === "approve" ? "Approve" : "Reject"} icon={dialog.action === "approve" ? "pi pi-check" : "pi pi-times"} className={`p-button-sm ${dialog.action === "approve" ? "p-button-success" : "p-button-danger"}`} onClick={handleSubmit} loading={approvalLoading} />
          </div>
        }
      >
        <div className="py-2">
          <div className="mb-3 p-3" style={{ background: "#f8f9fa", borderRadius: "6px" }}>
            <p className="m-0 text-600 text-sm">
              PO ID: <strong>{dialog.rowData?.pO_ID}</strong>
            </p>
            <p className="m-0 mt-1 text-sm">
              B Rate: <span style={{ color: "red" }}>{dialog.rowData?.p_BRate}</span>
              {" → "}
              <span style={{ color: "green" }}>{dialog.rowData?.r_BRate}</span>
            </p>
            <p className="m-0 mt-1 text-sm">
              S Rate: <span style={{ color: "red" }}>{dialog.rowData?.p_SRate}</span>
              {" → "}
              <span style={{ color: "green" }}>{dialog.rowData?.r_SRate}</span>
            </p>
            <p className="m-0 mt-1 text-sm">
              Margin: <span style={{ color: "red" }}>{dialog.rowData?.p_MRate}</span>
              {" → "}
              <span style={{ color: "green" }}>{dialog.rowData?.r_MRate}</span>
            </p>
          </div>

          {dialog.action === "reject" && (
            <div>
              <label className="font-semibold text-sm">
                Reason <span style={{ color: "red" }}>*</span>
              </label>
              <InputTextarea required value={dialog.reason} onChange={(e) => setDialog((prev) => ({ ...prev, reason: e.target.value }))} rows={3} className="w-full mt-1" placeholder="Enter  reason..." autoResize />
            </div>
          )}

          {dialog.action === "approve" && (
            <div className="flex align-items-center gap-2 mt-2">
              <i className="pi pi-exclamation-triangle" style={{ color: "#f59e0b", fontSize: "1.5rem" }} />
              <p className="m-0 text-600">Are you sure you want to approve this rate change request?</p>
            </div>
          )}
        </div>
      </Dialog>

      {/*            Dialog box for reason      */}

      <Dialog
        header={reasonDialog.title}
        visible={reasonDialog.visible}
        style={{ width: "400px" }}
        onHide={() => setReasonDialog({ visible: false, reason: "", title: "" })}
        footer={<Button label="Close" icon="pi pi-times" className="p-button-secondary p-button-sm" onClick={() => setReasonDialog({ visible: false, reason: "", title: "" })} />}
      >
        <div className="flex align-items-center gap-2 p-3">
          <i className="pi pi-info-circle text-red-500 text-2xl" />
          <span className="text-700">{reasonDialog.reason || "No reason provided."}</span>
        </div>
      </Dialog>

      <Dialog header={"PO Details"} visible={openPODetails.visible} style={{ width: "80vw" }} onHide={() => setOpenPODetails({ visible: false, singleData: {} })}>
        <DataTable style={{ border: "1px solid rgba(105,105,105,0.17)", borderTopWidth: "0", marginTop: "5px" }} ref={dt} size="small" stripedRows showGridlines responsiveLayout="scroll" exportFilename={format(new Date(), "dd-MM-yyyy hh_mm_ss a").toUpperCase()} value={[openPODetails.singleData]}>
          <Column field="sn" header="SN" body={(rowData, options) => options.rowIndex + 1} />
          <Column field="company_Name_Sales" header="Company Name" />
          <Column
            field="salesmanager"
            header="Sales Manager"
            sortable
            body={(rowData: any) => {
              return <span>{(rowData?.salesmanager || "").split("-")[0]}</span>;
            }}
          />
          <Column field="sales_Company_Branch" header="Sales Comp (Branch)" sortable />
          <Column
            field="user_Name"
            header="Recruiter"
            sortable
            body={(rowData: any) => {
              return <span>{(rowData?.user_Name || "").split("-")[0]}</span>;
            }}
          />
          <Column field="recruiter_Company_Branch" header="Recruiter Comp (Branch)" sortable />
          <Column field="consultant_Name" header="Consultant Name" />
          <Column field="req_CategoryName" header="Skill Category" style={{ maxWidth: "8rem", overflow: "hidden" }} />
          <Column field="project_Client_Name" header="End Client" />
          <Column field="project_Start_Date1" header="Start Date" style={{ minWidth: "100px" }} body={(rowData: any) => safeFormatDate(rowData.project_Start_Date1, "dd/MM/yyyy", DateFormate)} />
          <Column field="bRate" header="B Rate" />
          <Column field="sRate" header="S Rate" />
          <Column field="mRate" header="Margin " />
          <Column field="vendor_Company_Name" header="Vendor Company" />
          <Column header="Sub Vendor" body={(e) => (e.pO_Sub_Vendor_Company !== null ? <p>{e.pO_Sub_Vendor_Company}</p> : <p>{e.sub_Vendor_Company}</p>)} />
          <Column field="pO_Date1" header="PO Date" style={{ minWidth: "100px" }} body={(rowData: any) => safeFormatDate(rowData.pO_Date1, "dd/MM/yyyy", DateFormate)} />
          <Column field="pO_Status" header="Status" body={r => r.pO_Status ? POStatusMap[r.pO_Status] : ''} />
          <Column field="referred_By" header="Referred By" />
        </DataTable>
      </Dialog>
    </div>
  );
};
